'use client';

/**
 * Staff "Preview as a parent" entry on a class, open gym, private lesson or tryout page (#5634, KTD8/KTD9). Rendered
 * outside the page's call-to-action chain, so it shows on locked, members-only, ended and
 * evaluation-required pages too. Only the server's access answer shows it.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import {
  PREVIEW_AS_PARENT_HINT,
  PREVIEW_AS_PARENT_LABEL,
  PREVIEW_AS_PARENT_LESSON_HINT,
  PREVIEW_AS_PARENT_TRYOUT_HINT,
} from '@/utils/enrollmentPreview/previewCopy';
import type { PreviewSurface } from '@/utils/enrollmentPreview/previewLock';

const HINT_BY_SURFACE: Record<PreviewSurface, string> = {
  class: PREVIEW_AS_PARENT_HINT,
  private_lesson: PREVIEW_AS_PARENT_LESSON_HINT,
  tryout: PREVIEW_AS_PARENT_TRYOUT_HINT,
};


export default function PreviewAsParentButton({
  programId,
  surface,
  enrollHref,
  enabled,
  onStart,
}: {
  programId: string;
  /** Which sign-up page the preview opens; the server answers yes only for a program of that kind. */
  surface: PreviewSurface;
  /** The enroll or book page URL with `preview=1`. */
  enrollHref: string;
  /** Signed in, on a page of this surface. Nothing is asked otherwise. */
  enabled: boolean;
  /** Runs as the preview opens (the lesson page starts every preview with an empty preview cart). */
  onStart?: () => void;
}) {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    setAllowed(false);
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/enrollment-preview/access?programId=${encodeURIComponent(programId)}&surface=${surface}`,
        );
        const data = res.ok ? await res.json() : null;
        if (!cancelled) setAllowed(data?.allowed === true);
      } catch {
        // No answer, no button.
      }
    })();
    return () => { cancelled = true; };
  }, [programId, surface, enabled]);

  if (!allowed) return null;
  return (
    <div className="mb-4 space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4" data-testid="preview-as-parent">
      <p className="text-sm text-gray-600">
        {HINT_BY_SURFACE[surface]}
      </p>
      <Button variant="outline" className="w-full" asChild>
        <Link href={enrollHref} onClick={onStart}>
          <GoogleMaterialIcon name="visibility" size="sm" />
          {PREVIEW_AS_PARENT_LABEL}
        </Link>
      </Button>
    </div>
  );
}
